import axios, { AxiosInstance } from 'axios';
import { isDemoMode } from '../utils/demo';

/** Backend origin. Empty in production, where nginx serves the UI and proxies /api. */
export const API_BASE_URL: string = process.env.REACT_APP_API_BASE_URL ?? '';

const XSRF_COOKIE = 'XSRF-TOKEN';
const XSRF_HEADER = 'X-XSRF-TOKEN';

/** Reads a cookie by name. Returns undefined when absent (or when document is unavailable). */
export function readCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const prefix = `${name}=`;
  for (const part of document.cookie.split(';')) {
    const trimmed = part.trim();
    if (trimmed.startsWith(prefix)) {
      return decodeURIComponent(trimmed.slice(prefix.length));
    }
  }
  return undefined;
}

/**
 * 'unauthorized' (401): no login, or it expired. 'forbidden' (403): logged in, but this login has no trainer
 * account (for example a remember-me session). The app shows the login screen for the first and a
 * "log in again" message for the second. Neither redirects by itself, so there is no loop.
 */
export type SessionProblem = 'unauthorized' | 'forbidden';
type SessionListener = (problem: SessionProblem) => void;

const sessionListeners = new Set<SessionListener>();

/** Subscribes to 401 and 403 answers from the trainer API. Returns the unsubscribe function. */
export function onSessionProblem(listener: SessionListener): () => void {
  sessionListeners.add(listener);
  return () => {
    sessionListeners.delete(listener);
  };
}

export const trainerApi: AxiosInstance = axios.create({
  baseURL: `${API_BASE_URL}/api/trainer`,
  withCredentials: true,
  headers: { Accept: 'application/json' },
});

// Echo the CSRF cookie back as a header on every state-changing request.
trainerApi.interceptors.request.use(config => {
  const method = (config.method ?? 'get').toLowerCase();
  if (method !== 'get' && method !== 'head' && method !== 'options') {
    const token = readCookie(XSRF_COOKIE);
    if (token) {
      config.headers.set(XSRF_HEADER, token);
    }
  }
  return config;
});

// Tell the app when the session is missing (401) or has no trainer access (403).
trainerApi.interceptors.response.use(undefined, error => {
  const status = error?.response?.status;
  if (status === 401 || status === 403) {
    const problem: SessionProblem = status === 401 ? 'unauthorized' : 'forbidden';
    sessionListeners.forEach(listener => listener(problem));
  }
  return Promise.reject(error);
});

if (process.env.NODE_ENV !== 'production' && isDemoMode()) {
  // Demo fixtures are never bundled into production builds (NODE_ENV is replaced at build time).
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const { demoAdapter } = require('../dev/demoMock');
  trainerApi.defaults.adapter = demoAdapter;
  console.warn('[trainer] DEMO MODE: API responses are mocked. Disable with ?demo=off');
}

/** URL the browser is sent to for Google sign-in. */
export const GOOGLE_LOGIN_URL = `${API_BASE_URL}/oauth2/authorization/google`;
